import type { Editor } from "@tiptap/core";
import type { StyleRefUpdate, StyleUpdate, StyleUpdateBase } from "../interop/dotnetExports/bridge";
import type { TextStyleCssAttributes } from "../rich-text/converter";
import { BulletList } from "@tiptap/extension-list";
import { TextStyle } from "@tiptap/extension-text-style";
import { AxObjectType } from "../bridge/selection";
import { toInlineArgb } from "../common/utils";
import {
  setFitToContent,
  setSectionColorStyle,
  setSelectionGeometryNum,
  setSelectionStyleBool,
  setSelectionStyleNum,
  setSelectionStyleRef,
  setSelectionStyleRefColor,
  setSelectionStyleRefStr,
  setSelectionStylesNum,
  setSelectionStylesRef,
  setSelectionStyleStr,
  toggleSelectionListInfo,
} from "../interop/dotnetExports/bridge";
import { loadFont } from "../interop/dotnetExports/fontLoading";
import { useDiagramTextEditorsStore } from "../store/textEditor";
import {
  AxFontStyle,
  getCssFontSize,
  getCssFontStretch,
  getCssFontStyle,
  getCssLetterCasing,
  getCssLineHeight,
  getCssPixelNumber,
  getCssTextAlignment,
  TextListType,
} from "./attributedString";

export function updateDiagramEditorStyleReference(update: StyleRefUpdate): boolean {
  const { key, value, referenceId, referenceName, types = AxObjectType.All } = update;

  switch (key) {
    case "font-size":
      return setSelectionStyleRef(key, referenceId, referenceName, value, types);
    case "font-family":
      return setSelectionStyleRefStr(key, referenceId, referenceName, value, types);
    case "corner-radius":
      return setSelectionStylesRef(
        ["corner-radius-top-left", "corner-radius-top-right", "corner-radius-bottom-right", "corner-radius-bottom-left"],
        referenceId,
        referenceName,
        value,
        types,
      );
    case "corner-radius-bottom-left":
    case "corner-radius-bottom-right":
    case "corner-radius-top-left":
    case "corner-radius-top-right":
      return setSelectionStyleRef(key, referenceId, referenceName, value, types);
    case "fill":
    case "font-color":
    case "border-fill":
    case "outer-shadow-fill":
      return setSelectionStyleRefColor(key, referenceId, referenceName, value.value, types);
    case "outer-shadow":
    case "character-spacing":
    case "padding-left":
    case "padding-top":
    case "padding-right":
    case "padding-bottom":
      return setSelectionStyleRef(key, referenceId, referenceName, value, types);
    default:
      throw new Error(`Invalid style property key: ${key satisfies never}`);
  }
}

export function updateDiagramEditorStyle(update: StyleUpdate): boolean {
  const { key, value, types = AxObjectType.All } = update;

  switch (key) {
    case "fill":
    case "font-color":
    case "font-size":
    case "font-weight":
    case "line-spacing":
    case "character-spacing":
    case "horizontal-align":
    case "vertical-align":
    case "letter-casing":
    case "baseline":
    case "padding-left":
    case "padding-top":
    case "padding-right":
    case "padding-bottom":
    case "border-width":
    case "border-fill":
    case "border-visibility":
    case "corner-radius-bottom-left":
    case "corner-radius-bottom-right":
    case "corner-radius-top-left":
    case "corner-radius-top-right":
    case "outer-shadow-fill":
    case "outer-shadow-x":
    case "outer-shadow-y":
    case "outer-shadow-blur":
    case "outer-shadow-spread":
    case "inner-shadow-fill":
    case "inner-shadow-x":
    case "inner-shadow-y":
    case "inner-shadow-blur":
    case "inner-shadow-spread":
    case "rotation":
    case "opacity":
    case "line-pattern":
    case "line-start":
    case "line-end":
    case "start-cap":
    case "end-cap":
    case "line-join":
    case "stroke-alignment":
    case "miter-angle":
    case "widget-blur-radius":
    case "backdrop-blur-radius":
      return setSelectionStyleNum(key, value, types);
    case "loc-x":
    case "loc-y":
    case "width":
    case "height":
      return setSelectionGeometryNum(key, value.value, types, value.isScaling ?? false);
    case "padding-horizontal":
      return setSelectionStylesNum(["padding-left", "padding-right"], value, types);
    case "padding-vertical":
      return setSelectionStylesNum(["padding-top", "padding-bottom"], value, types);
    case "corner-radius":
      return setSelectionStylesNum(["corner-radius-top-left", "corner-radius-top-right", "corner-radius-bottom-right", "corner-radius-bottom-left"], value, types);
    case "font-family":
      return setSelectionStyleStr(key, value, types);
    case "fit-to-image":
    case "inner-shadow-enabled":
    case "strikethrough":
    case "underline":
    case "italic":
    case "visibility":
    case "list-info":
    case "shadow-enabled":
    case "widget-blur-enabled":
    case "backdrop-blur-enabled":
      return setSelectionStyleBool(key, value, types);
    case "auto-fit-height":
    case "auto-fit-width":
      setFitToContent(key, value);
      return true;
    case "fixed-width":
    case "fixed-height":
    case "font-stretch":
    case "font-style":
    case "outer-shadow":
      console.error(`Style property key "${key}" doesn't have a handler implemented.`);
      return false;
    default:
      throw new Error(`Invalid style property key: ${key satisfies never}`);
  }
}

function buildChain(editor: Editor, update: StyleUpdateBase) {
  const chain = editor.chain();

  const {
    focus = "text-editor",
  } = update;

  if (focus === "text-editor") {
    chain.focus();
  }

  return chain;
}

export function updateTextEditorStyle(editor: Editor, update: StyleUpdate): void {
  const { key, value } = update;

  const chain = buildChain(editor, update);

  switch (key) {
    case "font-family":
      chain.unsetFontFamilyReference().setFontFamily(value).run();
      break;
    case "underline":
      if (value) {
        chain.setUnderline().run();
      } else {
        chain.unsetUnderline().run();
      }
      break;
    case "strikethrough":
      if (value) {
        chain.setStrike().run();
      } else {
        chain.unsetStrike().run();
      }
      break;
    case "italic":
      if (value) {
        chain.setMark(TextStyle.name, { fontStyle: getCssFontStyle(AxFontStyle.Italic) }).run();
      } else {
        chain.setMark(TextStyle.name, { fontStyle: getCssFontStyle(AxFontStyle.Normal) }).run();
      }
      break;
    case "font-size":
      chain.unsetFontSizeReference().setFontSize(getCssFontSize(value)).run();
      break;
    case "font-weight":
      chain.setMark(TextStyle.name, { fontWeight: value.toString() }).run();
      break;
    case "font-stretch":
      chain.setMark(TextStyle.name, { fontStretch: getCssFontStretch(value) }).run();
      break;
    case "font-color":
      chain.unsetColorReference().setMark(TextStyle.name, { color: toInlineArgb(value) }).run();
      break;
    case "character-spacing":
      chain.setMark(TextStyle.name, { letterSpacing: getCssPixelNumber(value) }).run();
      break;
    case "letter-casing":
      chain.setMark(TextStyle.name, { textTransform: getCssLetterCasing(value) }).run();
      break;
    case "horizontal-align":
      chain.setTextAlign(getCssTextAlignment(value)).run();
      break;
    case "line-spacing":
      chain.setLineHeight(getCssLineHeight(value)).run();
      break;
    case "list-info":
      if (editor.isActive(BulletList.name) !== value) {
        chain.toggleBulletList().run();
      }
      break;
    default:
      break;
  }

  const { fontFamily, fontWeight, fontStyle } = editor.getAttributes(TextStyle.name) as Partial<TextStyleCssAttributes>;
  if (fontFamily && fontWeight && fontFamily) {
    void loadFont(fontFamily, fontWeight, fontStyle === "italic");
  }
}

export function updateTextEditorStyleReference(editor: Editor, update: StyleRefUpdate): void {
  const { key, value, referenceId, referenceName } = update;

  const chain = buildChain(editor, update);

  switch (key) {
    case "font-size":
      chain.setFontSizeReference({ referenceId, referenceName }).setFontSize(getCssFontSize(value)).run();
      break;
    case "font-family":
      chain.setFontFamilyReference({ referenceId, referenceName }).setFontFamily(value).run();
      break;
    case "font-color": {
      chain.setColorReference({ referenceId, referenceName }).setMark(TextStyle.name, { color: toInlineArgb(value.value) }).run();
      break;
    }
    default:
      break;
  }

  const { fontFamily, fontWeight, fontStyle } = editor.getAttributes(TextStyle.name) as Partial<TextStyleCssAttributes>;
  if (fontFamily && fontWeight && fontFamily) {
    void loadFont(fontFamily, fontWeight, fontStyle === "italic");
  }
}

export function useToggleList() {
  const store = useDiagramTextEditorsStore();

  function toggleList(type: TextListType) {
    const activeEditor = store.activeEditor;
    if (!activeEditor) {
      toggleSelectionListInfo(type);
      return;
    }

    const chain = activeEditor.chain().focus();
    if (type === TextListType.Bullet) {
      chain.toggleBulletList();
    } else if (type === TextListType.Numbered) {
      chain.toggleOrderedList();
    } else if (type === TextListType.Task) {
      chain.toggleTaskList();
    } else {
      throw new Error(`Unrecognized text list type: ${type satisfies never}`);
    }

    chain.run();
  }

  return { toggleList };
}

export function updateSectionColor(bgColor: number) {
  setSectionColorStyle(bgColor);
}
